import { observer } from 'mobx-react';
import cls from 'classnames';

import Link, { NAV, NONE, REGULAR } from '@/common/components/Link/Link';
import { useLongPress } from '@/common/hooks/useLongPress/useLongPress';
import Notifier from '@/common/services/Notifier';
import URLProvider from '@/common/services/URLProvider';
import SetupPreset from '@/media/entity/SetupPreset';
import { ProductEntity } from '@/product/types';

import styles from './ProductLink.module.css';

interface ProductLinkProps {
  product: ProductEntity;
  setupPreset?: Nullable<SetupPreset>;
  text?: Nullable<string>;
  className?: string;
  appearance?: typeof REGULAR | typeof NAV | typeof NONE;
  external?: boolean;
  'aria-label'?: string;
}

function ProductLink(props: ProductLinkProps) {
  const {
    product,
    setupPreset = null,
    text = null,
    className,
    appearance = REGULAR,
    external = false,
    ...rest
  } = props;

  const title = text || product.shortView;

  const longPressHandlers = useLongPress(() => {
    navigator.clipboard.writeText(title);
    Notifier.success(`Product SKU copied to clipboard: ${title}`);
  });

  if (product.isReference) {
    return (
      <span
        className={cls(styles.productLink, className)}
        aria-label="Reference product SKU"
        {...rest}
        title={title}
        {...longPressHandlers}
      >
        {title}
      </span>
    );
  }

  return (
    <Link
      external={external}
      appearance={appearance}
      className={cls(styles.productLink, className)}
      to={URLProvider.productDetailPage(product, setupPreset)}
      aria-label="Product Link"
      title={title}
      {...rest}
      {...longPressHandlers}
    >
      {title}
    </Link>
  );
}

export default observer(ProductLink);
